/**
 * Enhanced Command Registry with Metadata
 * Loads and manages LaTeX command metadata with categories, arguments, and usage frequency
 */

import commandsData from '@assets/latex/commands.json'
import { CommandMetadata, CommandCategory, ArgumentSpec } from './types'
import { logDebug } from '@utils/logger'

// Map command names to their metadata
const commandRegistry = new Map<string, CommandMetadata>()

// Common commands with high usage frequency (for initial ranking)
const COMMON_COMMANDS = new Set([
  'begin', 'end', 'usepackage', 'documentclass', 'section', 'subsection',
  'subsubsection', 'paragraph', 'item', 'textbf', 'textit', 'emph',
  'cite', 'ref', 'label', 'caption', 'figure', 'table', 'equation',
  'frac', 'sqrt', 'sum', 'int', 'prod', 'alpha', 'beta', 'gamma',
  'delta', 'epsilon', 'theta', 'lambda', 'mu', 'pi', 'sigma', 'phi',
  'omega', 'cdot', 'times', 'div', 'pm', 'leq', 'geq', 'neq',
  'approx', 'infty', 'partial', 'nabla', 'rightarrow', 'leftarrow',
  'Rightarrow', 'Leftarrow', 'in', 'notin', 'subset', 'subseteq',
  'cup', 'cap', 'emptyset', 'forall', 'exists', 'neg', 'land', 'lor'
])

/**
 * Infer command category from command name
 */
function inferCategory(commandName: string, snippet?: string): CommandCategory {
  const lower = commandName.toLowerCase()
  
  // Structure commands
  if (['begin', 'end', 'section', 'subsection', 'subsubsection', 'paragraph', 
       'chapter', 'part', 'title', 'author', 'date', 'maketitle'].includes(lower)) {
    return CommandCategory.STRUCTURE
  }
  
  // Environment commands
  if (lower === 'begin' || lower === 'end') {
    return CommandCategory.ENVIRONMENT
  }
  
  // Package commands
  if (['usepackage', 'documentclass', 'input', 'include'].includes(lower)) {
    return CommandCategory.PACKAGE
  }
  
  // Citation commands
  if (['cite', 'citep', 'citet', 'citeyear', 'shortcite', 'ref', 'label', 
       'pageref', 'eqref'].includes(lower)) {
    return CommandCategory.CITATION
  }
  
  // Math commands
  if (['frac', 'sqrt', 'sum', 'int', 'prod', 'lim', 'sin', 'cos', 'tan',
       'log', 'ln', 'exp', 'alpha', 'beta', 'gamma', 'delta', 'epsilon',
       'theta', 'lambda', 'mu', 'pi', 'sigma', 'phi', 'omega', 'cdot',
       'times', 'div', 'pm', 'leq', 'geq', 'neq', 'approx', 'infty',
       'partial', 'nabla', 'rightarrow', 'leftarrow', 'in', 'notin',
       'subset', 'subseteq', 'cup', 'cap', 'emptyset', 'forall', 'exists',
       'neg', 'land', 'lor', 'Rightarrow', 'Leftarrow'].includes(lower)) {
    return CommandCategory.MATH
  }
  
  // Text formatting
  if (['textbf', 'textit', 'emph', 'textmd', 'textnormal', 'texttt',
       'textsc', 'underline', 'sout'].includes(lower)) {
    return CommandCategory.TEXT
  }
  
  return CommandCategory.CUSTOM
}

/**
 * Parse snippet to extract argument information
 */
function parseArguments(snippet?: string, commandName?: string): ArgumentSpec[] {
  if (!snippet) {
    // Infer from common patterns
    if (commandName === 'begin') {
      return [{ type: 'required', placeholder: 'env', suggestions: 'environments' }]
    }
    if (commandName === 'usepackage') {
      return [{ type: 'required', placeholder: 'package', suggestions: 'packages' }]
    }
    if (commandName === 'cite') {
      return [{ type: 'required', placeholder: 'key', suggestions: 'citations' }]
    }
    return []
  }
  
  const args: ArgumentSpec[] = []
  const placeholderRegex = /\$\{(\d+)(?::([^}]+))?\}/g
  let match
  
  while ((match = placeholderRegex.exec(snippet)) !== null) {
    const index = parseInt(match[1])
    const placeholder = match[2] || ''
    
    // Skip $0 (final cursor position)
    if (index === 0) continue
    
    args.push({
      type: 'required',
      placeholder: placeholder || `arg${index}`,
      suggestions: inferSuggestionType(placeholder, commandName)
    })
  }
  
  return args
}

/**
 * Infer suggestion type from placeholder text
 */
function inferSuggestionType(placeholder: string, commandName?: string): string[] | 'environments' | 'packages' | 'citations' | undefined {
  const lower = placeholder.toLowerCase()
  
  if (lower.includes('env') || lower === 'env') {
    return 'environments'
  }
  if (lower.includes('package') || lower === 'pkg') {
    return 'packages'
  }
  if (lower.includes('cite') || lower.includes('key') || lower === 'ref') {
    return 'citations'
  }
  
  return undefined
}

/**
 * Calculate usage frequency based on command commonality
 */
function calculateUsageFrequency(commandName: string): number {
  if (COMMON_COMMANDS.has(commandName.toLowerCase())) {
    return 100
  }
  
  // Medium frequency for structure commands
  const structureCommands = ['section', 'subsection', 'item', 'caption', 'label']
  if (structureCommands.includes(commandName.toLowerCase())) {
    return 80
  }
  
  // Lower frequency for specialized commands
  return 50
}

/**
 * Initialize command registry from JSON data
 */
export function initializeCommandRegistry(): void {
  const commands = commandsData as Record<string, { command: string; snippet?: string; detail?: string }>
  
  for (const [key, cmd] of Object.entries(commands)) {
    const commandName = cmd.command
    const category = inferCategory(commandName, cmd.snippet)
    const arguments_ = parseArguments(cmd.snippet, commandName)
    const usageFrequency = calculateUsageFrequency(commandName)
    
    const metadata: CommandMetadata = {
      name: commandName,
      category,
      arguments: arguments_,
      snippet: cmd.snippet,
      description: cmd.detail || `LaTeX command: ${commandName}`,
      usageFrequency,
      requiresMathMode: category === CommandCategory.MATH,
      requiresPackage: [],
      aliases: []
    }
    
    commandRegistry.set(commandName, metadata)
    
    // Also register by key if different
    if (key !== commandName) {
      commandRegistry.set(key, metadata)
    }
  }
  
  logDebug('[CommandRegistry] Initialized', { 
    totalCommands: commandRegistry.size 
  })
}

/**
 * Get command metadata by name
 */
export function getCommandMetadata(commandName: string): CommandMetadata | undefined {
  return commandRegistry.get(commandName) || 
         Array.from(commandRegistry.values()).find(cmd => 
           cmd.name === commandName || 
           cmd.aliases?.includes(commandName)
         )
}

/**
 * Get all commands
 */
export function getAllCommands(): CommandMetadata[] {
  return Array.from(commandRegistry.values())
}

/**
 * Find commands matching a query
 */
export function findCommands(query: string, category?: CommandCategory): CommandMetadata[] {
  const lowerQuery = query.toLowerCase()
  const matches: Array<{ command: CommandMetadata; score: number }> = []
  
  for (const cmd of commandRegistry.values()) {
    // Filter by category if specified
    if (category && cmd.category !== category) {
      continue
    }
    
    const cmdName = cmd.name.toLowerCase()
    let score = 0
    
    // Exact prefix match gets highest score
    if (cmdName.startsWith(lowerQuery)) {
      score = 100 - cmdName.length + cmd.usageFrequency / 10
    } else if (cmdName.includes(lowerQuery)) {
      score = 50 - cmdName.length + cmd.usageFrequency / 10
    }
    
    if (score > 0) {
      matches.push({ command: cmd, score })
    }
  }
  
  return matches
    .sort((a, b) => b.score - a.score)
    .slice(0, 50)
    .map(m => m.command)
}

/**
 * Get commands by category
 */
export function getCommandsByCategory(category: CommandCategory): CommandMetadata[] {
  return Array.from(commandRegistry.values())
    .filter(cmd => cmd.category === category)
    .sort((a, b) => b.usageFrequency - a.usageFrequency)
}

// Initialize on module load
initializeCommandRegistry()

